<script>
  import { contacts, friendGroups, viewStack } from '../../stores'
  import ViewHeader from '../ViewHeader'
  import ContactSelectionItem from '../ContactSelectionItem.svelte'
  import { contactsSort } from '../../functions/contacts-sort.js'

  let currentView = 'LIST' // 'LIST', 'EDIT', 'CREATE'
  let editingGroup = null
  let editingName = ''
  let allContacts = []
  let errorMessage = null

  $: allSelected = allContacts.length > 0 && allContacts.every(c => c.selected)

  function toggleSelectAll() {
    const target = !allSelected
    allContacts = allContacts.map(c => ({ ...c, selected: target }))
  }

  function setErrorMessage (event) {
    errorMessage = event.detail
  }

  function startCreate() {
    editingGroup = null
    editingName = ''
    errorMessage = null
    setupContacts()
    currentView = 'CREATE'
  }

  function startEdit(group) {
    editingGroup = group
    editingName = group.name
    errorMessage = null
    setupContacts(group)
    currentView = 'EDIT'
  }

  function setupContacts(group = null) {
    const sortedContacts = [...$contacts].sort(contactsSort)
    allContacts = sortedContacts.map(c => ({
      contact: c,
      selected: group ? group.contactIds.includes(c.id) : false
    }))
  }

  async function saveGroup() {
    if (!editingName.trim()) return

    const selectedContactIds = allContacts
      .filter(c => c.selected)
      .map(c => c.contact.id)

    const groupToSave = {
      name: editingName.trim(),
      contactIds: selectedContactIds
    }

    if (editingGroup && editingGroup.id != null) {
      groupToSave.id = editingGroup.id
    }

    await friendGroups.set(groupToSave)
    currentView = 'LIST'
  }

  async function deleteGroup(group) {
    if (window.confirm(`Are you sure you want to delete the group "${group.name}"?`)) {
      await friendGroups.delete(group.id)
      currentView = 'LIST'
    }
  }

  function getMemberNames(group) {
    const names = []
    for (const id of group.contactIds) {
      const contact = $contacts.find(c => c.id === id)
      if (contact) {
        names.push(contact.displayName)
      }
    }
    return names.join(', ')
  }
</script>

{#if currentView === 'LIST'}
  <ViewHeader>Friend Groups</ViewHeader>

  <div class="groups-list">
    {#if $friendGroups.length === 0}
      <div class="empty-state">
        <svg class="feather-icon empty-icon"><use href="feather-sprite.svg#users" /></svg>
        <p>No friend groups created yet.</p>
        <p class="empty-info">Create groups to quickly select and send photos to multiple friends at once.</p>
      </div>
    {:else}
      {#each $friendGroups as group}
        <button class="-button-reset group-card" on:click={() => startEdit(group)} type="button">
          <div class="group-info">
            <span class="group-name">{group.name}</span>
            <span class="group-members">
              {#if group.contactIds.length === 0}
                No friends in this group
              {:else}
                {group.contactIds.length} friend{group.contactIds.length === 1 ? '' : 's'}: {getMemberNames(group)}
              {/if}
            </span>
          </div>
          <span class="edit-btn">
            <svg class="feather-icon"><use href="feather-sprite.svg#chevron-right" /></svg>
          </span>
        </button>
      {/each}
    {/if}
  </div>

  <div class="footer-actions">
    <button class="-button-primary full-width" on:click={startCreate} type="button">
      <svg class="feather-icon"><use href="feather-sprite.svg#plus" /></svg>
      Create Friend Group
    </button>
  </div>
{:else}
  <ViewHeader popWhenClose={false} on:close={() => currentView = 'LIST'}>
    {currentView === 'CREATE' ? 'Create Group' : 'Edit Group'}
  </ViewHeader>

  <div class="form">
    <label for="groupName" class="label">Group Name</label>
    <input
      id="groupName"
      class="input group-name-input"
      bind:value={editingName}
      placeholder="e.g. Besties, Family, Coworkers"
    />
  </div>

  <div class="members-section">
    <h3>Select Group Members</h3>
    
    {#if errorMessage}
      <p class="text-color-error">{errorMessage}</p>
    {/if}

    {#if allContacts.length === 0}
      <p class="no-contacts-info">
        You don't have any contacts yet.
      </p>
    {:else}
      <div class="list">
        {#if allContacts.length > 1}
          <label class="select-all">
            <input type="checkbox" checked={allSelected} on:change={toggleSelectAll} />
            <span>Select All</span>
          </label>
        {/if}

        {#each allContacts as c}
          <ContactSelectionItem
            contact={c.contact}
            bind:selected={c.selected}
            on:errorMessage={setErrorMessage}
          />
        {/each}
      </div>
    {/if}
  </div>

  <footer class="edit-footer">
    <div class="button-row">
      <button class="-button-secondary" on:click={() => currentView = 'LIST'} type="button">Cancel</button>
      
      {#if currentView === 'EDIT'}
        <button class="-button-secondary delete-button" on:click={() => deleteGroup(editingGroup)} type="button" title="Delete Group">
          <svg class="feather-icon"><use href="feather-sprite.svg#trash-2" /></svg>
        </button>
      {/if}

      <button class="-button-primary save-button" disabled={!editingName.trim()} on:click={saveGroup} type="button">
        Save Group
      </button>
    </div>
  </footer>
{/if}

<style>
  .groups-list {
    margin: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    overflow-y: auto;
    max-height: calc(var(--viewport-height) - 150px);
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1.5rem;
    text-align: center;
    background-color: var(--light-grey);
    border-radius: var(--content-box-border-radius);
    margin-top: 1rem;
  }

  .empty-icon {
    width: 3rem;
    height: 3rem;
    color: var(--grey);
    margin-bottom: 1rem;
  }

  .empty-info {
    font-size: 0.85rem;
    color: var(--dark-grey);
    margin-top: 0.5rem;
  }

  .group-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background-color: var(--light-grey);
    border-radius: var(--content-box-border-radius);
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
    border: 1px solid transparent;
  }

  .group-card:hover {
    background-color: var(--dark-light-grey);
  }

  .group-card:active {
    transform: scale(0.98);
  }

  .group-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex-grow: 1;
    min-width: 0;
  }

  .group-name {
    font-weight: bold;
    font-size: 1.1rem;
    color: var(--black);
  }

  .group-members {
    font-size: 0.85rem;
    color: var(--dark-grey);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .edit-btn {
    padding: 0.2rem;
    color: var(--dark-grey);
  }

  .footer-actions {
    padding: 1rem;
    background-color: var(--white);
    border-top: 1px solid var(--light-grey);
  }

  .group-name-input {
    border: 2px solid var(--grey) !important;
    border-radius: var(--content-box-border-radius) !important;
    padding: 0.8rem 1rem !important;
    transition: border-color 0.2s ease;
  }

  .group-name-input:focus {
    border-color: var(--brand-color) !important;
    outline: none;
  }

  .members-section {
    margin-top: 1.5rem;
  }

  .members-section h3 {
    font-size: 1rem;
    text-transform: uppercase;
    color: var(--dark-grey);
    margin-bottom: 0.5rem;
  }

  .no-contacts-info {
    color: var(--dark-grey);
    font-style: italic;
    margin: 1rem;
  }

  .list {
    margin: 0.8rem 1rem 1.5rem 1rem;
  }

  .select-all {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--light-grey);
    margin-bottom: 0.8rem;
  }

  .edit-footer {
    position: sticky;
    bottom: 0;
    background-color: var(--white);
    padding: 0.8rem 1rem;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
    z-index: 100;
  }

  .button-row {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    max-width: var(--max-width);
    margin: 0 auto;
  }

  .delete-button {
    border-color: var(--error-color) !important;
    color: var(--error-color) !important;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem !important;
  }

  .save-button {
    flex-grow: 1;
  }
</style>
